import React, { useEffect, useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from '@mui/material'
import * as Yup from 'yup'
import AddIcon from '@mui/icons-material/Add'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'
import DeleteIcon from '@mui/icons-material/Delete'
import { message } from 'antd'

import CircularLoading from '../../../../../components/CircularProgress'
import { IPipeline, IPipelineItem } from '../../../../../types/pipeline'
import { axiosInstance } from '../../../../../hooks/useAxios'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'

interface IPipelineFormModalProps {
  isOpen: boolean;
  onClose: (visible: boolean) => void;
  pipeline?: IPipeline;
  onUpdatePipelineListSuccess: () => void;
}

interface IPipelineFormModal {
  name: string;
  items: IPipelineItem[]
}

const getPipelineFormValidationSchema = () => {
  return Yup.object({
    name: Yup.string().required('กรุณากรอกชื่อ pipeline'),
    items: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('กรุณากรอกชื่อ item'),
      }),
    ).required('กรุณากรอก items'),
  })
}

export const PipelineFormModal = (props: IPipelineFormModalProps) => {
  const { isOpen, onClose, pipeline, onUpdatePipelineListSuccess } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    setIsLoading(true)
    formik.setValues(
      {
        name: pipeline?.name ?? '',
        items: pipeline?.items ?? [
          {
            name: 'เริ่มต้น',
          },
          {
            name: '',
          },
          {
            name: 'สิ้นสุด',
          },
        ],
      },
    )
    setIsLoading(false)
  }, [isOpen, pipeline])

  const formik = useFormik<IPipelineFormModal>({
    initialValues: {
      name: '',
      items: [],
    } as IPipelineFormModal,
    validationSchema: getPipelineFormValidationSchema(),
    onSubmit: async (values: IPipelineFormModal) => {
      setIsLoading(true)

      const isUpdate = !!pipeline
      try {
        await axiosInstance.request({
          method: isUpdate ? 'PUT' : 'POST',
          url: isUpdate ? `/pipeline/${pipeline.objectId}` : '/pipeline',
          data:
          {
            ...values,
          },
        })
        message.success(isUpdate ? 'แก้ไข pipeline สำเร็จ' : 'เพิ่ม pipeline สำเร็จ')
        formik.resetForm()
        onClose(false)
        onUpdatePipelineListSuccess()
      } catch (e: any) {
        const messageError = ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] || 'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="xl" fullWidth={true}>
      <CircularLoading isLoading={isLoading} />
      <DialogTitle>{pipeline ? 'แก้ไข pipeline' : 'เพิ่ม pipeline'}</DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <TextField
                id="name"
                fullWidth
                label="ชื่อ"
                name="name"
                helperText={
                  formik.touched.name && formik.errors.name as string
                }
                error={
                  !!(formik.touched.name && formik.errors.name)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </Grid>
            <Grid item xs={12} lg={12}
              sx={{
                overflowX: 'auto',
              }}>
              <Stepper alternativeLabel>
                {formik.values.items.map((step, index) => {
                  return (
                    <Step key={index}>
                      <StepLabel>
                        <TextField
                          fullWidth
                          sx={{
                            minWidth: 150,
                          }}
                          id={`items[${index}].name`}
                          name={`items[${index}].name`}
                          label="ชื่อขั้นตอน"
                          value={step.name ?? ''}
                          onChange={(e) => {
                            return (
                              formik.setFieldValue(`items[${index}].name`, e.target.value)
                            )
                          }}
                          onBlur={formik.handleBlur}
                          error={formik.touched.items?.[index]?.name && !!(formik.errors.items?.[index])}
                          helperText={formik.touched.items?.[index]?.name && !!(formik.errors.items?.[index])}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {(index !== formik.values.items.length - 1 && formik.values.items.length < 10) &&
                                  <IconButton onClick={() => {
                                    const newItems = [...formik.values.items]
                                    newItems.splice(index + 1, 0, { name: '' })
                                    formik.setFieldValue('items', newItems)
                                  }}>
                                    <AddIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                }
                                {index > 0 && (index !== formik.values.items.length - 1) &&
                                  <IconButton onClick={() => {
                                    const newItems = [...formik.values.items]
                                    newItems.splice(index, 1)
                                    formik.setFieldValue('items', newItems)
                                  }}>
                                    <DeleteIcon sx={{ fontSize: 20 }} />
                                  </IconButton>
                                }
                              </InputAdornment>
                            ),
                          }}
                        />
                      </StepLabel>
                    </Step>
                  )
                })}
              </Stepper>
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                {pipeline ? 'แก้ไข pipeline' : 'เพิ่ม pipeline'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
